import { useTransition, animated } from 'react-spring'
import styled from 'styled-components'

import { color } from '../styles'
import { SPACING, TRANSITION_TIME_MD_MS } from '../styles/mixins/constants'
import { device } from '../styles/mixins/device'

import { TakeoverContainer } from './TakeoverContainer'

export type PanelChildFn<D = null> = (props: { data: D | null; isVisible: boolean }) => JSX.Element

export type PanelProps<D = null> = {
  onClose?: () => void
  className?: string
  data?: D | null
  children?: JSX.Element | JSX.Element[]
  renderChild?: PanelChildFn<D>
  isVisible?: boolean
}

export const Panel = <D,>(props: PanelProps<D>): JSX.Element => {
  const { className, children, isVisible, onClose, data = null, renderChild } = props
  const transformTransition = useTransition(isVisible, {
    from: {
      transform: 'translateX(100%)',
    },
    enter: {
      transform: 'translateX(0)',
    },
    leave: {
      transform: 'translateX(100%)',
    },
    config: {
      duration: TRANSITION_TIME_MD_MS,
    },
  })

  return (
    <>
      <div>
        {transformTransition((style, item, _, key) =>
          item ? (
            <ContentBoundary style={style} key={key}>
              <Content className={className}>
                {renderChild
                  ? renderChild({
                      data,
                      isVisible: Boolean(isVisible),
                    })
                  : children}
              </Content>
            </ContentBoundary>
          ) : null
        )}
      </div>
      <TakeoverContainer isVisible={isVisible} onClose={onClose} />
    </>
  )
}

const Content = styled.div`
  background-color: ${color.background.app};
  display: flex;
  height: 100%;
  padding: ${SPACING[6]};
`
const ContentBoundary = styled(animated.div)`
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: 1001;
  top: 0;
  right: 0;
  ${device.laptop`
    max-width: 530px;
  `}
`

const Overlay = styled(animated.div)`
  background-color: rgba(0, 0, 0, 0.5);
  width: 100vw;
  height: 100vh;
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;
`

Panel.Overlay = Overlay
Panel.Content = ContentBoundary
